import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { BUTTON_CLASSES, BUTTON_SIZE_SM } from './Button'
import { markDirty, clearDirty } from '../lib/unsavedChanges'

/** Selector for elements that can receive focus inside the dialog. */
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Accessible modal shell. Owns the behaviors every dialog needs so the
 * feature dialogs don't each re-implement them:
 *  - role="dialog" + aria-modal + aria-labelledby wired to the heading
 *  - moves focus into the dialog on open (first focusable, else the card)
 *  - traps Tab / Shift+Tab within the dialog
 *  - returns focus to the element that was focused before opening, on close
 *  - Escape closes; backdrop click closes — both ignored while `busy`
 *  - when `dirty`, registers an unsaved-work key (so the app-wide
 *    `beforeunload` guard fires on tab close / navigation) and asks in a
 *    row inside the dialog before closing via Escape or backdrop click
 *
 * Visual shell matches what the dialogs previously hand-rolled: a
 * `bg-ink/40` scrim + a centered surface card with header / body / footer.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  busy = false,
  dirty = false,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  /** Optional sub-line rendered under the heading (game details, helper copy). */
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  size?: 'md' | 'lg' | 'xl'
  busy?: boolean
  /** When the dialog holds unsaved input: registers an unsaved-work key and
   *  guards every close behind an in-dialog prompt. */
  dirty?: boolean
}) {
  const headingId = useId()
  const guardKey = useId()
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  // Element that had focus before the dialog opened — focus returns here on close.
  const restoreRef = useRef<HTMLElement | null>(null)
  const keepEditingRef = useRef<HTMLButtonElement>(null)
  // Element focused when the discard prompt opened; Keep editing returns here.
  const beforePromptRef = useRef<HTMLElement | null>(null)

  // Move focus to the prompt's safe choice when it appears; that also scrolls it
  // into view inside the overflow card.
  useEffect(() => {
    if (confirmingDiscard) keepEditingRef.current?.focus()
  }, [confirmingDiscard])

  const keepEditing = () => {
    setConfirmingDiscard(false)
    const prior = beforePromptRef.current
    ;(prior?.isConnected && cardRef.current?.contains(prior) ? prior : cardRef.current)?.focus()
  }

  // A reopened dialog starts without a pending discard prompt.
  useEffect(() => {
    if (!open) setConfirmingDiscard(false)
  }, [open])

  // Register an unsaved-work key while open AND dirty; clear it on close, when
  // dirty goes false, or on unmount. Feeds the app-wide beforeunload guard.
  useEffect(() => {
    if (!open || !dirty) return
    markDirty(guardKey)
    return () => clearDirty(guardKey)
  }, [open, dirty, guardKey])

  // Close, but confirm first if there's unsaved input. Used by Escape and the
  // backdrop click; an explicit footer button is the caller's concern.
  const requestClose = () => {
    if (dirty) {
      if (!confirmingDiscard) beforePromptRef.current = document.activeElement as HTMLElement | null
      setConfirmingDiscard(true)
      return
    }
    onClose()
  }

  // Capture the trigger and move focus into the dialog when it opens; restore
  // focus to the trigger when it closes/unmounts.
  useEffect(() => {
    if (!open) return
    restoreRef.current = document.activeElement as HTMLElement | null
    const card = cardRef.current
    if (card) {
      // Prefer the dialog's own content over the header close button, so focus
      // lands on the first field or action as it did before the button existed.
      const first =
        card.querySelector<HTMLElement>(`:is(${FOCUSABLE}):not([data-dialog-close])`) ??
        card.querySelector<HTMLElement>(FOCUSABLE)
      ;(first ?? card).focus()
    }
    return () => {
      const trigger = restoreRef.current
      if (trigger?.isConnected) {
        trigger.focus?.()
        return
      }
      // The trigger is routinely GONE by the time the dialog closes: applying
      // an edit clears the selection that rendered it. `focus()` on a detached
      // node is a silent no-op, which drops focus to <body> and makes a
      // keyboard operator tab through the whole shell and grid again. Fall
      // back to the main landmark, the same place this app's skip link goes.
      // Queried by tag rather than by id because only admin-spa's shell gives
      // it one, and made focusable here for the shells that have not.
      const main = document.querySelector('main')
      if (!main) return
      if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1')
      main.focus()
    }
  }, [open])

  // Escape to close (ignored while busy) + focus trap on Tab.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (!busy) requestClose()
        return
      }
      if (e.key !== 'Tab') return
      const card = cardRef.current
      if (!card) return
      const focusable = Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      )
      if (focusable.length === 0) {
        e.preventDefault()
        card.focus()
        return
      }
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      const active = document.activeElement
      if (e.shiftKey && (active === first || active === card)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, busy, onClose, dirty])

  if (!open) return null

  const sizeClass = { md: 'max-w-md', lg: 'max-w-2xl', xl: 'max-w-4xl' }[size]
  const headerPad = size === 'md' ? 'px-5 py-3' : 'px-6 py-4'
  const headingSize = size === 'md' ? 'text-base font-bold' : 'text-xl font-bold'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      // Backdrop click closes, unless an operation is in flight.
      onClick={() => {
        if (!busy) requestClose()
      }}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        className={`max-h-[90vh] w-full ${sizeClass} overflow-y-auto rounded-lg border border-line bg-surface shadow-xl focus:outline-none`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className={`flex items-start justify-between gap-3 border-b border-line ${headerPad}`}>
          <div className="min-w-0">
            <h2 id={headingId} className={`${headingSize} tracking-tight text-ink`}>
              {title}
            </h2>
            {description ? (
              <div className={`text-ink-soft ${size === 'md' ? 'mt-0.5 text-xs' : 'mt-1 text-sm'}`}>
                {description}
              </div>
            ) : null}
          </div>
          {busy ? null : (
            <button
              type="button"
              onClick={requestClose}
              className="rounded-md p-1.5 text-ink-soft hover:bg-fill"
              aria-label="Close"
              data-dialog-close=""
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </header>
        {confirmingDiscard ? (
          <div
            role="alert"
            className="flex items-center justify-between gap-3 border-b border-line bg-brand-gold/10 px-5 py-2.5 text-sm text-ink"
          >
            <span>Discard your changes?</span>
            <span className="flex gap-2">
              <button
                type="button"
                ref={keepEditingRef}
                className={`${BUTTON_CLASSES.secondary} ${BUTTON_SIZE_SM}`}
                onClick={keepEditing}
              >
                Keep editing
              </button>
              <button
                type="button"
                className={`${BUTTON_CLASSES.danger} ${BUTTON_SIZE_SM}`}
                disabled={busy}
                onClick={() => {
                  setConfirmingDiscard(false)
                  onClose()
                }}
              >
                Discard
              </button>
            </span>
          </div>
        ) : null}
        {children}
        {footer}
      </div>
    </div>
  )
}
